import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from 'react';
import { Box, Typography } from '@mui/material';
import { BryntumGrid } from '@bryntum/schedulerpro-react';
import type { Grid, GridColumnConfig, Model } from '@bryntum/schedulerpro';
import { TaskInfo } from '@dispatch/shared/types';
import { escapeHtml } from '../utils/escapeHtml';

interface UnassignedGridProps {
  tasks: TaskInfo[];
  isLoading?: boolean;
  onTaskClick?: (task: TaskInfo) => void;
  onTaskSchedule?: (
    taskId: string,
    params: {
      resourceId: string;
      startDate: string;
      endDate: string;
    }
  ) => Promise<void>;
}

/**
 * UnassignedGrid - Grid component for displaying unassigned tasks
 *
 * Tasks can be dragged from this grid to the scheduler to assign them
 */
export const UnassignedGrid = forwardRef<
  { instance: unknown },
  UnassignedGridProps
>(({ tasks, isLoading = false, onTaskClick }, ref) => {
  const gridRef = useRef<BryntumGrid>(null);
  const handleCellClick = useCallback(
    (event: { grid: Grid; record: Model }) => {
      // Bryntum Grid records are typed as `Model`, but our grid `data` prop
      // passes `TaskInfo[]`, so each record carries the original payload in `.data`.
      const record = event.record as Model & { data?: TaskInfo };
      const task = record?.data;
      if (task && onTaskClick) {
        onTaskClick(task);
      }
    },
    [onTaskClick]
  );

  useImperativeHandle(ref, () => ({
    get instance() {
      return gridRef.current?.instance;
    },
  }));

  const columns = useMemo(
    () => [
      {
        type: 'template' as const,
        flex: 1,
        field: 'clientInfo',
        cellCls: 'unscheduled-name-cell',
        template: ({ record }: { record: TaskInfo }) => `
            <div class="unassigned-task-info">
              <div class="task-insured">${escapeHtml(record?.insuredName)}</div>
              <div class="task-claim">${escapeHtml(record?.claimNumber)}</div>
            </div>
          `,
      },
      {
        type: 'template' as const,
        field: 'taskDetails',
        flex: 1,
        template: ({ record }: { record: TaskInfo }) => `
            <div class="unassigned-task-location">
              <div class="task-location">${escapeHtml(
                record?.lossCity
              )}, ${escapeHtml(record?.lossState)}</div>
              <div class="task-appointment">${escapeHtml(
                record?.appointment
              )}</div>
            </div>
          `,
      },
      {
        type: 'template' as const,
        flex: 1,
        field: 'taskType',
        template: ({ record }: { record: TaskInfo }) => `
            <div class="unassigned-task-type">
              <div class="task-method">${escapeHtml(record?.peril)}</div>
              <div class="task-duration">${
                record?.appointmentDurationInMinutes || 0
              } min</div>
            </div>
          `,
      },
    ],
    []
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: 350,
        borderLeft: '1px solid',
        borderColor: 'divider',
        borderTopRightRadius: '5px',
        borderBottomRightRadius: '5px',
      }}
    >
      <Box
        sx={{
          p: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          Unassigned Assignments
        </Typography>
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <BryntumGrid
          ref={gridRef}
          id="scheduler-grid"
          hideHeaders={true}
          selectionMode={{
            row: true,
            cell: false,
            multiSelect: false,
          }}
          stripeFeature={true}
          // Our column config includes custom renderer properties that Bryntum's
          // `GridColumnConfig` type doesn't declare — double cast bridges the gap.
          columns={columns as unknown as GridColumnConfig[]}
          rowHeight={65}
          disableGridRowModelWarning={true}
          data={tasks}
          loadMask={isLoading ? 'Loading...' : null}
          onCellClick={handleCellClick}
        />
      </Box>
    </Box>
  );
});

UnassignedGrid.displayName = 'UnassignedGrid';
